import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import {
  Observable,
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  map,
  of,
  switchMap,
  takeWhile,
  tap,
  throwError,
} from 'rxjs';
import { GkPostalCode } from '../../../gk-dynamic-form/services/postal-code/postal-code.model';
import { GkPostalCodeService } from '../../../gk-dynamic-form/services/postal-code/postal-code.service';
import { FieldId } from '../../../gk-dynamic-list/services/dictionary/dictionary.model';
import {
  CommunityGusDictionaryRequestBody,
  CommunityGusField,
} from '../../../services/community-gus-dictionary/community-gus-dictionary.model';
import { CommunityGusDictionaryService } from '../../../services/community-gus-dictionary/community-gus-dictionary.service';
import { CountryDictionaryService } from '../../../services/country-dictionary/country-dictionary.service';
import { DistrictGusField } from '../../../services/district-gus-dictionary/district-gus-dictionary.model';
import { DistrictGusDictionaryService } from '../../../services/district-gus-dictionary/district-gus-dictionary.service';
import { FormlyBaseConfigService } from '../../../services/formly/formly-base-config/formly-base-config.service';
import { selectedFromListValidator } from '../../../services/formly/validators/selected-from-list.validator';
import { PlaceGusDictionaryRequestBody } from '../../../services/place-gus-dictionary/place-gus-dictionary.model';
import { PlaceGusDictionaryService } from '../../../services/place-gus-dictionary/place-gus-dictionary.service';
import { StreetGusDictionaryService } from '../../../services/street-gus-dictionary copy/street-gus-dictionary.service';
import { VoivodeshipGusDictionaryService } from '../../../services/voivodeship-gus-dictionary/voivodeship-gus-dictionary.service';
import {
  AddressSelectFormConfig,
  AddressSelectFormControlName,
  AddressSelectFormModel,
  isPolandCountrySelected,
} from './address-select-form.model';

@Injectable()
export class AddressSelectFormService extends FormlyBaseConfigService<
  AddressSelectFormConfig,
  AddressSelectFormModel,
  AddressSelectFormControlName
> {
  constructor(
    private translateService: TranslateService,
    private countryDictionaryService: CountryDictionaryService,
    private voivodeshipGusDictionaryService: VoivodeshipGusDictionaryService,
    private districtGusDictionaryService: DistrictGusDictionaryService,
    private communityGusDictionaryService: CommunityGusDictionaryService,
    private placeGusDictionaryService: PlaceGusDictionaryService,
    private streetGusDictionaryService: StreetGusDictionaryService,
    private postalCodeService: GkPostalCodeService,
  ) {
    super();
  }

  getFormConfig(): Observable<{
    fields: FormlyFieldConfig[];
    options?: FormlyFormOptions;
  }> {
    const defaultFieldConfig: FormlyFieldConfig = {
      className: 'flex-grow-1 col',
      type: 'select',
      wrappers: ['gk-common-bootstrap-form-field'],
      props: {
        labelProp: 'name',
        valueProp: 'id',
        wrapperClass: 'my-1',
        required: true,
        attributes: {
          class: 'form-select form-select-sm',
        },
        validationMessages: {
          required: this.translateService.stream('FIELD_REQUIRED'),
        },
      },
    };

    const fields = [
      {
        fieldGroupClassName:
          'd-flex flex-column flex-md-row column-gap-4 flex-wrap',
        fieldGroup: [
          {
            key: AddressSelectFormControlName.Country,
            hooks: {
              onInit: (field: FormlyFieldConfig): void => {
                this.countryDictionaryService.get().subscribe((data) => {
                  field.props.options = data;
                  field.formControl.setValue(
                    CountryDictionaryService.polandCountryId,
                  );
                });
              },
            },
            expressions: {
              'props.disabled': 'field.options?.formState.disableGroup',
            },
          },
          {
            key: AddressSelectFormControlName.Voivodeship,
            hooks: {
              onInit: (field: FormlyFieldConfig): void => {
                this.voivodeshipGusDictionaryService.get().subscribe((data) => {
                  field.props.options = data;
                });
              },
              afterViewInit: (field: FormlyFieldConfig): void => {
                const countryControl = field.parent.get(
                  AddressSelectFormControlName.Country,
                ).formControl;
                countryControl.valueChanges
                  .pipe(distinctUntilChanged())
                  .subscribe(() => {
                    field.formControl.setValue(null);
                  });
              },
            },
            expressions: {
              'props.disabled': (field: FormlyFieldConfig): boolean =>
                field.options?.formState.disableGroup ||
                !field.model[AddressSelectFormControlName.Country],
              hide: (field: FormlyFieldConfig): boolean =>
                !isPolandCountrySelected(
                  field.model[AddressSelectFormControlName.Country],
                ),
            },
          },
          {
            key: AddressSelectFormControlName.District,
            props: {
              valuePrimitive: false,
              valueProp: (value: DistrictGusField): DistrictGusField => value,
              compareWith: (
                dictionaryField1: DistrictGusField,
                dictionaryField2: DistrictGusField,
              ): boolean => dictionaryField1?.id === dictionaryField2?.id,
            },
            hooks: {
              afterViewInit: (field: FormlyFieldConfig): void => {
                const voivodeshipControl = field.parent.get(
                  AddressSelectFormControlName.Voivodeship,
                ).formControl;
                if (!voivodeshipControl) {
                  return;
                }
                voivodeshipControl.valueChanges
                  .pipe(
                    distinctUntilChanged(),
                    switchMap((voivodeshipId: number | string) => {
                      field.formControl.setValue(null);
                      if (voivodeshipId) {
                        return this.districtGusDictionaryService.get(
                          voivodeshipId,
                        );
                      } else {
                        return of([]);
                      }
                    }),
                  )
                  .subscribe((districts) => {
                    field.props.options = districts;
                    field.props.disabled = !districts || districts.length === 0;
                    field.formControl.setValue(null);
                  });
              },
            },
            expressions: {
              'props.disabled': (field: FormlyFieldConfig): boolean =>
                field.options?.formState.disableGroup ||
                !field.model[AddressSelectFormControlName.Voivodeship],
              hide: (field: FormlyFieldConfig): boolean =>
                !isPolandCountrySelected(
                  field.model[AddressSelectFormControlName.Country],
                ),
            },
          },
          {
            key: AddressSelectFormControlName.Community,
            props: {
              labelProp: 'displayName',
              valuePrimitive: false,
              valueProp: (value: CommunityGusField): CommunityGusField => value,
              compareWith: (
                dictionaryField1: CommunityGusField,
                dictionaryField2: CommunityGusField,
              ): boolean => dictionaryField1?.id === dictionaryField2?.id,
            },
            hooks: {
              afterViewInit: (field: FormlyFieldConfig): void => {
                const districtControl = field.parent.get(
                  AddressSelectFormControlName.District,
                ).formControl;
                if (!districtControl) {
                  return;
                }
                districtControl.valueChanges
                  .pipe(
                    distinctUntilChanged(),
                    switchMap((district: DistrictGusField) => {
                      field.formControl.setValue(null);
                      if (district) {
                        return this.communityGusDictionaryService.get(
                          CommunityGusDictionaryRequestBody.fromDistrictGus(
                            district,
                          ),
                        );
                      } else {
                        return of([]);
                      }
                    }),
                  )
                  .subscribe((communities) => {
                    field.props.options = communities;
                    field.props.disabled =
                      !communities || communities.length === 0;
                    field.formControl.setValue(null);
                  });
              },
            },
            expressions: {
              'props.disabled': (field: FormlyFieldConfig): boolean =>
                field.options?.formState.disableGroup ||
                !field.model[AddressSelectFormControlName.District],
              hide: (field: FormlyFieldConfig): boolean =>
                !isPolandCountrySelected(
                  field.model[AddressSelectFormControlName.Country],
                ),
            },
          },
        ].map(this.createFieldWithDefaults(defaultFieldConfig)),
      },
      {
        fieldGroupClassName:
          'd-flex flex-column flex-md-row column-gap-4 flex-wrap',
        fieldGroup: [
          {
            key: AddressSelectFormControlName.Place,
            props: {
              labelProp: 'displayName',
            },
            hooks: {
              afterViewInit: (field: FormlyFieldConfig): void => {
                let isInitialValueChange = true;
                const trySubscribe = (): void => {
                  const communityControl = field.form.get(
                    AddressSelectFormControlName.Community,
                  );
                  if (!communityControl) {
                    setTimeout(trySubscribe, 50);
                    return;
                  }
                  communityControl.valueChanges
                    .pipe(
                      distinctUntilChanged(),
                      filter(() =>
                        isPolandCountrySelected(
                          field.model[AddressSelectFormControlName.Country],
                        ),
                      ),
                      switchMap((community: CommunityGusField) => {
                        if (
                          !isInitialValueChange &&
                          !!field.formControl.value
                        ) {
                          field.formControl.setValue(null);
                        }
                        isInitialValueChange = false;
                        if (community) {
                          return this.placeGusDictionaryService.get(
                            PlaceGusDictionaryRequestBody.fromCommunityGus(
                              community,
                            ),
                          );
                        } else {
                          return of([]);
                        }
                      }),
                    )
                    .subscribe((places) => {
                      field.props.options = places;
                      field.props.disabled = !places || places.length === 0;
                      field.formControl.setValue(null);
                    });

                  const currentValue = communityControl.value;
                  if (currentValue !== undefined) {
                    communityControl.setValue(currentValue);
                  }
                };
                trySubscribe();
              },
            },
            expressions: {
              'props.disabled': (field: FormlyFieldConfig): boolean =>
                field.options?.formState.disableGroup ||
                !field.model[AddressSelectFormControlName.Community],
              hide: (field: FormlyFieldConfig): boolean =>
                !isPolandCountrySelected(
                  field.model[AddressSelectFormControlName.Country],
                ),
            },
          },
          {
            key: AddressSelectFormControlName.Place,
            type: 'input',
            props: {
              labelProp: undefined,
              valueProp: undefined,
              attributes: {
                class: 'form-control form-control-sm',
              },
            },
            hooks: {
              afterViewInit: (field: FormlyFieldConfig): void => {
                const countryControl = field.form.get(
                  AddressSelectFormControlName.Country,
                );
                countryControl.valueChanges
                  .pipe(distinctUntilChanged())
                  .subscribe(() => {
                    field.formControl.setValue(null);
                  });
              },
            },
            expressions: {
              'props.disabled': (field: FormlyFieldConfig): boolean =>
                field.options?.formState.disableGroup ||
                isPolandCountrySelected(
                  field.model[AddressSelectFormControlName.Country],
                ),
              hide: (field: FormlyFieldConfig): boolean =>
                isPolandCountrySelected(
                  field.model[AddressSelectFormControlName.Country],
                ),
            },
          },
          {
            key: AddressSelectFormControlName.Street,
            hooks: {
              afterViewInit: (field: FormlyFieldConfig): void => {
                const placeControl = field.parent.get(
                  AddressSelectFormControlName.Place,
                ).formControl;
                placeControl.valueChanges
                  .pipe(
                    distinctUntilChanged(),
                    filter(() =>
                      isPolandCountrySelected(
                        field.model[AddressSelectFormControlName.Country],
                      ),
                    ),
                    switchMap((placeId: FieldId) => {
                      field.formControl.setValue(null);
                      if (placeId) {
                        return this.streetGusDictionaryService.get(placeId);
                      } else {
                        return of([]);
                      }
                    }),
                  )
                  .subscribe((streets) => {
                    field.props.options = streets;
                    field.props.disabled = !streets || streets.length === 0;
                    field.formControl.setValue(null);
                  });
              },
            },
            expressions: {
              'props.disabled': (field: FormlyFieldConfig): boolean =>
                field.options?.formState.disableGroup ||
                !field.model[AddressSelectFormControlName.Place],
              hide: (field: FormlyFieldConfig): boolean =>
                !isPolandCountrySelected(
                  field.model[AddressSelectFormControlName.Country],
                ),
            },
          },
          {
            key: AddressSelectFormControlName.Street,
            type: 'input',
            props: {
              labelProp: undefined,
              valueProp: undefined,
              attributes: {
                class: 'form-control form-control-sm',
              },
            },
            hooks: {
              afterViewInit: (field: FormlyFieldConfig): void => {
                const countryControl = field.form.get(
                  AddressSelectFormControlName.Country,
                );
                countryControl.valueChanges
                  .pipe(distinctUntilChanged())
                  .subscribe(() => {
                    field.formControl.setValue(null);
                  });
              },
            },
            expressions: {
              'props.disabled': (field: FormlyFieldConfig): boolean =>
                field.options?.formState.disableGroup ||
                isPolandCountrySelected(
                  field.model[AddressSelectFormControlName.Country],
                ),
              hide: (field: FormlyFieldConfig): boolean =>
                isPolandCountrySelected(
                  field.model[AddressSelectFormControlName.Country],
                ),
            },
          },
          {
            key: AddressSelectFormControlName.BuildingNumber,
            type: 'input',
            props: {
              labelProp: undefined,
              valueProp: undefined,
              attributes: {
                class: 'form-control form-control-sm',
              },
            },
            hooks: {
              afterViewInit: (field: FormlyFieldConfig): void => {
                const countryControl = field.form.get(
                  AddressSelectFormControlName.Country,
                );
                countryControl.valueChanges
                  .pipe(distinctUntilChanged())
                  .subscribe(() => {
                    field.formControl.setValue(null);
                  });
              },
            },
            expressions: {
              'props.disabled': 'field.options?.formState.disableGroup',
            },
          },
          {
            key: AddressSelectFormControlName.LocalNumber,
            type: 'input',
            props: {
              labelProp: undefined,
              valueProp: undefined,
              required: false,
              attributes: {
                class: 'form-control form-control-sm',
              },
            },
            hooks: {
              afterViewInit: (field: FormlyFieldConfig): void => {
                const countryControl = field.form.get(
                  AddressSelectFormControlName.Country,
                );
                countryControl.valueChanges
                  .pipe(distinctUntilChanged())
                  .subscribe(() => {
                    field.formControl.setValue(null);
                  });
              },
            },
            expressions: {
              'props.disabled': 'field.options?.formState.disableGroup',
            },
          },
          {
            key: AddressSelectFormControlName.PostalCode,
            type: 'gk-formly-typeahead',
            props: {
              placeholder: 'XX-XXX',
              attributes: {
                class: 'form-control form-control-sm',
              },
              typeahead: {
                search: this.searchPostalCode,
                formatter: this.defaultFormatter,
                resultFormatter: this.defaultFormatter,
              },
              onBlur: (field: FormlyFieldConfig): void =>
                this.setPostalCodeByNameOnBlur(field),
            },
            hooks: {
              afterViewInit: (field: FormlyFieldConfig): void => {
                const countryControl = field.form.get(
                  AddressSelectFormControlName.Country,
                );
                countryControl.valueChanges
                  .pipe(distinctUntilChanged())
                  .subscribe(() => {
                    field.formControl.setValue(null);
                  });
              },
            },
            validators: {
              selectedFromList: {
                expression: (control: AbstractControl): boolean =>
                  selectedFromListValidator(control),
                message: this.translateService.stream(
                  'ADDRESS_FORM.VALUE_FROM_LIST',
                ),
              },
            },
            expressions: {
              hide: (field: FormlyFieldConfig): boolean =>
                !isPolandCountrySelected(
                  field.model[AddressSelectFormControlName.Country],
                ),
              'props.disabled': 'field.options?.formState.disableGroup',
            },
          },
          {
            key: AddressSelectFormControlName.PostalCode,
            type: 'input',
            wrappers: ['gk-common-bootstrap-form-field'],
            props: {
              labelProp: undefined,
              valueProp: undefined,
              attributes: {
                class: 'form-control form-control-sm',
              },
            },
            expressions: {
              hide: (field: FormlyFieldConfig): boolean =>
                isPolandCountrySelected(
                  field.model[AddressSelectFormControlName.Country],
                ),
              'props.disabled': 'field.options?.formState.disableGroup',
            },
          },
          {
            key: AddressSelectFormControlName.PostOffice,
            type: 'input',
            props: {
              labelProp: undefined,
              valueProp: undefined,
              attributes: {
                class: 'form-control form-control-sm',
              },
            },
            hooks: {
              afterViewInit: (field: FormlyFieldConfig): void => {
                const countryControl = field.form.get(
                  AddressSelectFormControlName.Country,
                );
                countryControl.valueChanges
                  .pipe(distinctUntilChanged())
                  .subscribe(() => {
                    field.formControl.setValue(null);
                  });
              },
            },
            expressions: {
              'props.disabled': (field: FormlyFieldConfig): boolean =>
                field.options?.formState.disableGroup ||
                isPolandCountrySelected(
                  field.model[AddressSelectFormControlName.Country],
                ),
              hide: (field: FormlyFieldConfig): boolean =>
                isPolandCountrySelected(
                  field.model[AddressSelectFormControlName.Country],
                ),
            },
          },
        ].map(this.createFieldWithDefaults(defaultFieldConfig)),
      },
    ];

    return this.getFieldsWithCustomConfig(fields, this.getLabel).pipe(
      map((fieldsWithCustomConfig) => {
        return {
          fields: fieldsWithCustomConfig,
        };
      }),
      catchError((error) => {
        console.error(error);

        return throwError(() => error);
      }),
    );
  }

  searchPostalCode = ($text: Observable<string>): Observable<GkPostalCode[]> =>
    $text.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      filter((term) => term.length >= 5),
      switchMap((term) => this.postalCodeService.getPostalCodes(term)),
    );

  defaultFormatter = (result: any): string =>
    _.isString(result) ? result : result.name;

  setPostalCodeByNameOnBlur(field: FormlyFieldConfig): void {
    const formControl = field.formControl;
    if (
      formControl?.value &&
      !_.get(formControl.value, 'id') &&
      _.isString(formControl.value)
    ) {
      this.postalCodeService
        .getPostalCodes(formControl.value)
        .pipe(
          takeWhile(
            () =>
              !_.get(formControl.value, 'id') && _.isString(formControl.value),
          ),
        )
        .subscribe((postalCodes) => {
          if (
            _.get(postalCodes, '[0].name', '').toUpperCase() ===
            formControl.value.toUpperCase()
          ) {
            formControl.setValue(postalCodes[0]);
          }
        });
    }
  }

  getLabel = (controlKey: string): Observable<string> => {
    return this.translateService.stream(
      `ADDRESS_SELECT_FORM.${controlKey.toUpperCase()}`,
    );
  };

  setAddressByIds(
    address: AddressSelectFormModel,
    form: FormGroup,
    fields: FormlyFieldConfig[],
  ): void {
    const countryField = this.findFieldByKey(fields, 'country');
    const voivodeshipField = this.findFieldByKey(fields, 'voivodeship');
    const districtField = this.findFieldByKey(fields, 'district');
    const communityField = this.findFieldByKey(fields, 'community');
    const placeField = this.findFieldByKey(fields, 'place');
    const streetField = this.findFieldByKey(fields, 'street');

    if (isPolandCountrySelected(address.country)) {
      this.countryDictionaryService
        .get()
        .pipe(
          tap((countries) => {
            if (countryField) countryField.props.options = countries;
            form
              .get(AddressSelectFormControlName.Country)
              ?.setValue(address.country);
          }),
          filter(() => !!address.country),
          switchMap(() => this.voivodeshipGusDictionaryService.get()),
          tap((voivodeships) => {
            if (voivodeshipField) voivodeshipField.props.options = voivodeships;
            const formControl = form.get(
              AddressSelectFormControlName.Voivodeship,
            );
            formControl?.enable();
            formControl?.setValue(address.voivodeship, { emitEvent: false });
          }),
          filter(() => !!address.voivodeship),
          switchMap(() =>
            this.districtGusDictionaryService.get(address.voivodeship),
          ),
          tap((districts) => {
            if (districtField) districtField.props.options = districts;
            const district = districts.find(
              (d) => d.id === address.district?.id,
            );
            const formControl = form.get(AddressSelectFormControlName.District);
            formControl?.enable();
            formControl?.setValue(district, { emitEvent: false });
          }),
          filter(() => !!address.district),
          switchMap(() =>
            this.communityGusDictionaryService.get(
              CommunityGusDictionaryRequestBody.fromDistrictGus(
                address.district,
              ),
            ),
          ),
          tap((communities: CommunityGusField[]) => {
            if (communityField) communityField.props.options = communities;
            const community = communities.find(
              (c) => c.id === address.community?.id,
            );
            const formControl = form.get(
              AddressSelectFormControlName.Community,
            );
            formControl?.enable();
            formControl?.setValue(community, { emitEvent: false });
          }),
          filter(() => !!address.community),
          switchMap(() =>
            this.placeGusDictionaryService.get(
              PlaceGusDictionaryRequestBody.fromCommunityGus(address.community),
            ),
          ),
          tap((places) => {
            if (placeField) placeField.props.options = places;
            const formControl = form.get(AddressSelectFormControlName.Place);
            formControl?.enable();
            formControl?.setValue(address.place, { emitEvent: false });
          }),
          filter(() => !!address.place),
          switchMap(() => this.streetGusDictionaryService.get(address.place)),
          tap((streets) => {
            if (streetField) streetField.props.options = streets;
            if (streets?.length === 0) {
              return;
            }
            const formControl = form.get(AddressSelectFormControlName.Street);
            formControl?.enable();
            formControl?.setValue(address.street, { emitEvent: true });
          }),
          tap(() => {
            form
              .get(AddressSelectFormControlName.BuildingNumber)
              ?.setValue(address.buildingNumber, { emitEvent: true });
            form
              .get(AddressSelectFormControlName.LocalNumber)
              ?.setValue(address.localNumber, { emitEvent: true });
            form
              .get(AddressSelectFormControlName.PostalCode)
              ?.setValue(address.postalCode, { emitEvent: true });
          }),
        )
        .subscribe();
    } else {
      this.countryDictionaryService
        .get()
        .pipe(
          switchMap((countries) => {
            if (countryField) countryField.props.options = countries;
            form
              .get(AddressSelectFormControlName.Country)
              ?.setValue(address.country);

            return of(true);
          }),
          delay(300),
          tap(() => {
            form
              .get(AddressSelectFormControlName.Voivodeship)
              ?.setValue(address.voivodeship, { emitEvent: false });
            form
              .get(AddressSelectFormControlName.District)
              ?.setValue(address.district, { emitEvent: false });
            form
              .get(AddressSelectFormControlName.Community)
              ?.setValue(address.community, { emitEvent: false });
            form
              .get(AddressSelectFormControlName.Place)
              ?.setValue(address.place, { emitEvent: false });
            form
              .get(AddressSelectFormControlName.Street)
              ?.setValue(address.street, { emitEvent: false });
            form
              .get(AddressSelectFormControlName.BuildingNumber)
              ?.setValue(address.buildingNumber, {
                emitEvent: true,
              });
            form
              .get(AddressSelectFormControlName.LocalNumber)
              ?.setValue(address.localNumber, {
                emitEvent: true,
              });
            form
              .get(AddressSelectFormControlName.PostalCode)
              ?.setValue(address.postalCode, { emitEvent: true });
            form
              .get(AddressSelectFormControlName.PostOffice)
              ?.setValue(address.postOffice, { emitEvent: true });
          }),
        )
        .subscribe();
    }
  }
}
